import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import type { DialogProps } from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { noop } from 'lodash';
import { PropsWithChildren, useCallback } from 'react';

import { Disable, Sxable } from 'library/types';

import { PageButtons } from '../buttons/PageButtons';

export type DialogCloseProps = {
	onClose?: () => void;
};

export interface BaseDialogProps extends Sxable, Disable, DialogCloseProps {
	closeButtonText?: string;
	closeOnConfirm?: boolean;
	confirmButtonText?: string;
	isSubmitting?: boolean;
	open?: boolean;
	onConfirm?: () => Promise<void> | void;
	maxWidth?: DialogProps['maxWidth'];
	title?: string;
	withCloseIcon?: boolean;
}

/**
 * Base component to use for creating dialogs.
 * @param {boolean} [withCloseIcon=true] - Can be used to hide close icon, but beware, as on mobile view the dialog is covering whole screen
 */
export const BaseDialog = ({
	children,
	closeButtonText,
	closeOnConfirm = false,
	confirmButtonText,
	disabled = false,
	isSubmitting = false,
	onConfirm,
	onClose = noop,
	open = true,
	maxWidth,
	sx,
	title = '',
	withCloseIcon = true,
}: PropsWithChildren<BaseDialogProps>) => {
	const fullScreen = useMediaQuery(({ breakpoints }: Theme) =>
		breakpoints.only('xs')
	);

	const handleOnConfirm = useCallback(async () => {
		if (typeof onConfirm === 'function') {
			const returnVal = onConfirm();
			if (returnVal instanceof Promise) {
				await returnVal;
			}
		}

		if (closeOnConfirm) {
			onClose();
		}
	}, [onConfirm, closeOnConfirm, onClose]);

	return (
		<Dialog
			className="base-dialog__root"
			open={open}
			onClose={onClose}
			fullScreen={fullScreen}
			maxWidth={maxWidth !== undefined ? maxWidth : 'sm'}
			fullWidth
			sx={sx}>
			{title && (
				<DialogTitle
					className="base-dialog__title"
					sx={{
						padding: ({ spacing }) => ({
							sm: spacing(2, 4, 2, 3),
						}),
					}}>
					{title}
				</DialogTitle>
			)}
			{withCloseIcon && onClose && (
				<IconButton
					className="base-dialog__title-close-button"
					aria-label="close"
					onClick={onClose}
					sx={{
						position: 'absolute',
						right: 0,
						top: 0,
						color: ({
							palette: {
								gray: { dark },
							},
						}) => dark,
					}}>
					<CloseIcon />
				</IconButton>
			)}
			<DialogContent className="base-dialog__content">
				{children}
				{confirmButtonText && (
					<PageButtons
						primaryButtonDisabled={disabled}
						primaryButtonLabel={confirmButtonText}
						secondaryButtonLabel={closeButtonText}
						primaryButtonAction={handleOnConfirm}
						secondaryButtonAction={onClose}
						withDivider={false}
						displayLoadingIndicator={isSubmitting}
					/>
				)}
			</DialogContent>
		</Dialog>
	);
};
